import { FC, useCallback } from "react";

import { translateOutcomeScoreReport } from "./utils";

import { serverGetOutcomeScoreReport } from "api/api-server-assessment";
import { OutcomeScoreTableData } from "api/api-types-assessment";
import { ScrollableTableContainer } from "components/tables/ScrollableTableContainer";
import { TD, TH, THead, TR, Table } from "components/tables/TableElements";
import { t } from "i18n/i18n";
import { ScoreIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectCurrentSectionId } from "store/selectors";
import { isOutcomeScoreTableDataSufficient } from "utils/assessment";

type OutcomeScoreTableProps = {
  outcomeScoreTableData: OutcomeScoreTableData | null;
};

export const OutcomeScoreTable: FC<OutcomeScoreTableProps> = ({ outcomeScoreTableData }) => {
  const sectionId = useAppSelector(selectCurrentSectionId);

  const exportData = useCallback(async () => {
    if (!sectionId) return { headers: [], rows: [] };
    const reportData = await serverGetOutcomeScoreReport({
      section_id: sectionId,
    });
    return translateOutcomeScoreReport(reportData);
  }, [sectionId]);

  if (!outcomeScoreTableData || !isOutcomeScoreTableDataSufficient(outcomeScoreTableData)) {
    return null;
  }

  const { headers, rows } = outcomeScoreTableData;

  return (
    <ScrollableTableContainer
      exportData={exportData}
      title={t("assessment_dashboard.student_score_per_outcome_title")}
      titleTooltipText={t("assessment_dashboard.student_score_per_outcome_tooltip")}
    >
      <Table>
        {/* Headers: <blank> / Outcome A / Outcome B / Outcome C */}
        {/* One row per student, showing each student's aggregate outcome scores */}
        <THead>
          <TR className="border-black-tint-70">
            <TH className="w-6/12 pl-4">
              {t("assessment_dashboard.all.student_name_header_title")}
            </TH>

            {headers.map((header) => (
              <TH
                key={header.id}
                verticalDivClassName="max-h-[180px]"
                verticalDivTitle={header.name}
                isVertical
              >
                {header.name}
              </TH>
            ))}
          </TR>
        </THead>
        <tbody>
          {rows.map((row) => (
            <TR key={row.student_id}>
              <TD className="max-w-40 truncate py-2.5 pl-[17px]" title={row.student_name}>
                {row.student_name}
              </TD>

              {row.outcome_scores.map((score, index) => (
                <TD key={headers[index].id}>
                  <ScoreIcon value={score ?? NaN} />
                </TD>
              ))}
            </TR>
          ))}
        </tbody>
      </Table>
    </ScrollableTableContainer>
  );
};
